/**
 * Кавычки вокруг значения, подставленного в текст для человека.
 *
 * Название организации, сценария, номера или узла человек нередко набирает
 * вместе с кавычками — и портал добавлял свои поверх его собственных:
 * «Учётная запись создана, организация «ООО «Ромашка»» заведена». Заплатка
 * в каждом сообщении по отдельности не годится: мест, где название попадает
 * в кавычки, полтора десятка, и следующее заведут снова без неё.
 *
 * Поэтому правило одно, и живёт оно здесь: снаружи всегда ёлочки, а кавычки,
 * которые человек набрал сам, становятся внутренними лапками — ровно так
 * вложенные кавычки и оформляют по-русски. Вторых ёлочек подряд не бывает
 * никогда.
 *
 * Почему в контрактах, а не в портале, где правило написали впервые. Тексты,
 * которые читает человек, рождаются в двух местах: портал пишет свои сам, а
 * движок сценариев — свои, и они приходят в портал уже готовыми строками
 * («У узла „Список из выгрузки“ поля заданы не списком»). Смотреть в
 * `apps/admin` движку нельзя и не нужно, а два одинаковых правила в двух
 * местах — это два правила, которые разойдутся. Контракты — единственный
 * общий предок портала и движка, и правило оформления подстановки такой же
 * общий договор между ними, как формат графа: разойдись он, одно и то же
 * имя узла выглядело бы в замечании валидатора и в отказе прогона
 * по-разному.
 *
 * Ничего из проекта модуль не импортирует (ADR-003) и импортировать не
 * может: это чистая работа со строкой.
 */

/** Пары кавычек, которые встречаются во введённом: ёлочки, лапки, прямые. */
const ПАРЫ: readonly (readonly [string, string])[] = [
  ['«', '»'],
  ['„', '“'],
  ['"', '"'],
]

/**
 * Снимает кавычки, которыми человек обвёл значение целиком: они значат то
 * же самое, что и наши, и оставить их значит показать две пары подряд.
 * Циклом — на случай «„Ромашка“» из чужой копипасты.
 */
function безВнешнихКавычек(значение: string): string {
  let текст = значение
  let снято = true
  while (снято && текст.length >= 2) {
    снято = false
    for (const [начало, конец] of ПАРЫ) {
      if (текст.startsWith(начало) && текст.endsWith(конец)) {
        текст = текст.slice(начало.length, текст.length - конец.length).trim()
        снято = true
        break
      }
    }
  }
  return текст
}

/**
 * Внутренние кавычки — лапками. Прямая кавычка не различает открывающую и
 * закрывающую, поэтому они считаются по порядку: первая открывает, вторая
 * закрывает.
 */
function вЛапки(текст: string): string {
  let открыта = false
  let итог = ''
  for (const символ of текст) {
    if (символ === '«' || символ === '„') {
      итог += '„'
      открыта = true
    } else if (символ === '»' || символ === '“') {
      итог += '“'
      открыта = false
    } else if (символ === '"') {
      итог += открыта ? '“' : '„'
      открыта = !открыта
    } else {
      итог += символ
    }
  }
  return итог
}

/**
 * Значение в кавычках. Единственный способ поставить кавычки вокруг
 * подставленного значения: `«${имя}»` в разметке и `"${имя}"` в сообщении
 * движка — две формы одной и той же ошибки, и обе ловятся проверками
 * (quotes.test.ts в портале, messages.test.ts в движке).
 */
export function вКавычках(значение: string): string {
  return `«${вЛапки(безВнешнихКавычек(значение.trim()))}»`
}
